import qs from 'qs';
import logger from 'config/logger';
import { CORRELATION_ID } from 'constants/headers';
import { CONTENT_STACK } from 'constants/services';
import { RequestClient } from 'request';

/** This functionality should not be referenced from client side code. Clients should use the BFF */
const { CMS_API_KEY, CMS_AUTH_TOKEN, CMS_ENV } = process?.env ?? {};

const contentClient = new RequestClient(
  'https://cdn.contentstack.io/v3/content_types'
);

const stripContentStackMeta = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((val) => stripContentStackMeta(val));
  }

  /* eslint-disable camelcase */
  const {
    _in_progress,
    _metadata,
    _version,
    ACL,
    content_type,
    created_at,
    created_by,
    file_size,
    filename,
    is_dir,
    locale,
    parent_uid,
    publish_details,
    tags,
    updated_at,
    updated_by,
    ...rest
  } = obj;
  /* eslint-enable camelcase */

  const objectPropertyMap = Object.entries(rest).map(([key, value]) => [
    key,
    stripContentStackMeta(value),
  ]);
  return Object.fromEntries(objectPropertyMap);
};

/**
 * getContentData is used to perform server side
 * requests to contentstack api
 *
 * Takes an object with an id field which represents the entries uid for a contentstack
 * content type. Also accepts an optional params field which can be used to implement content
 * stack queries and other advanced calls to the contentstack api.
 *
 * If a params.query object is passed it will be automatically stringified as JSON to
 * fulfill the contentstack query api requirements - this is for convenience and can be
 * skipped by passing options.stringifyParamsQuery false.
 *
 * For information on contentstack calls to get entries see:
 * https://www.contentstack.com/docs/developers/apis/content-delivery-api/#entries
 *
 * For information on using queries to refine results see:
 * see https://www.contentstack.com/docs/developers/apis/content-delivery-api/#queries
 * @async
 * @function getContentData
 * @param options
 * @param {string} options.id - required contentstack entries id
 * @param {string|object} [options.params={}] - extra url query strings to pass to contentstack query
 * @param {boolean} [options.stringifyParamsQuery=true] - if params contains a query field, it is stringified by default, pass false to skip this
 * @returns {Promise} The returned contentstack data or undefined in case of an error
 */
async function getContentData({
  id,
  params = {},
  stringifyParamsQuery = true,
  correlationId,
  log = logger,
}) {
  try {
    if (
      stringifyParamsQuery &&
      params.query &&
      typeof params.query !== 'string'
    ) {
      params.query = JSON.stringify(params.query);
    }

    const { entries } = await contentClient.get(`${id}/entries`, {
      correlationId,
      serviceName: CONTENT_STACK,
      log,
      headers: {
        /* eslint-disable camelcase */
        access_token: CMS_AUTH_TOKEN,
        api_key: CMS_API_KEY,
        [CORRELATION_ID]: correlationId,
        /* eslint-enable camelcase */
      },
      searchParams: qs.stringify({
        env: CMS_ENV,
        ...params,
      }),
    });

    if (!entries) {
      throw new Error('Unexpected response from ContentStack');
    }

    return { data: stripContentStackMeta(entries) };
  } catch (err) {
    logger.error(err, 'Content stack data fetch failed for id: %s', id);
  }
}

export default getContentData;
